/**
 * Subscribe Form Handler - Embassy Terazza
 * Integrates intl-tel-input, handles validation, submission gating, 
 * and controls the "Thank You" modal overlay.
 */
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';

const initSubscribeForm = () => {
  const form = document.getElementById('subscribe-form');
  const submit = document.getElementById('subscribe-submit');
  const overlay = document.getElementById('thankyou-overlay');
  const closeBtn = document.getElementById('thankyou-close');
  if (!form || !submit) return;

  const nameInput = document.getElementById('sub-name');
  const mobileInput = document.getElementById('sub-mobile');
  const emailInput = document.getElementById('sub-email');
  const consentInput = document.getElementById('sub-consent');

  const errorName = document.getElementById('error-name');
  const errorMobile = document.getElementById('error-mobile');
  const errorEmail = document.getElementById('error-email');
  const errorConsent = document.getElementById('error-consent');

  const fieldName = document.getElementById('field-name');
  const fieldMobile = document.getElementById('field-mobile');
  const fieldEmail = document.getElementById('field-email');

  const iti = intlTelInput(mobileInput, {
    initialCountry: 'in',
    countrySearch: false,
    separateDialCode: true,
    countrySelectorMode: 'DROPDOWN',
    strictMode: true,
  });

  const updateDialCode = () => {
    const prefixEl = document.getElementById('mobile-prefix');
    if (iti && typeof iti.getSelectedCountryData === 'function') {
      const countryData = iti.getSelectedCountryData();
      if (countryData && countryData.dialCode) {
        if (prefixEl) prefixEl.textContent = `+${countryData.dialCode}`;
        const countryCodeInput = document.getElementById('00N28000009Ffj0') || document.querySelector('input[name="00N28000009Ffj0"]');
        if (countryCodeInput) {
          countryCodeInput.value = countryData.dialCode;
        }
      }
    }
  };

  updateDialCode();

  mobileInput.addEventListener('countrychange', updateDialCode);
  mobileInput.addEventListener('change', updateDialCode);
  mobileInput.addEventListener('input', updateDialCode);

  const itiContainer = form.querySelector('.iti');
  if (itiContainer) {
    itiContainer.addEventListener('click', () => {
      setTimeout(updateDialCode, 10);
      setTimeout(updateDialCode, 100);
    });

    const observer = new MutationObserver(() => {
      updateDialCode();
    });
    observer.observe(itiContainer, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  iti.promise.then(() => {
    updateDialCode();
    mobileInput.addEventListener('countrychange', () => {
      updateDialCode();
      if (mobileInput.value) {
        if (isValidPhone()) {
          clearError(fieldMobile, errorMobile);
          setValid(fieldMobile);
        } else {
          setError(fieldMobile, errorMobile);
        }
      }
      validate();
    });
  });

  // Prevent Lenis from hijacking scroll events inside the country list dropdown
  const countryList = form.querySelector('.iti__country-list');
  if (countryList) {
    countryList.setAttribute('data-lenis-prevent', '');
  }

  // Validators
  const isValidName = (v) => v.trim().length >= 2;
  const isValidPhone = () => iti.isValidNumber();
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  // State UI setters
  const setValid = (fieldEl) => {
    if (!fieldEl) return;
    fieldEl.classList.add('is-valid');
    fieldEl.classList.remove('subscribe-section__field--error');
  };

  const setError = (fieldEl, errorEl) => {
    if (fieldEl) {
      fieldEl.classList.add('subscribe-section__field--error');
      fieldEl.classList.remove('is-valid');
    }
    if (errorEl) errorEl.classList.add('is-visible');
  };

  const clearError = (fieldEl, errorEl) => {
    if (fieldEl) fieldEl.classList.remove('subscribe-section__field--error');
    if (errorEl) errorEl.classList.remove('is-visible');
  };

  const clearValid = (fieldEl) => {
    if (fieldEl) fieldEl.classList.remove('is-valid');
  };

  const validate = () => {
    const allOk =
      isValidName(nameInput.value) &&
      isValidPhone() &&
      isValidEmail(emailInput.value) &&
      consentInput.checked;

    submit.disabled = !allOk;
    return allOk;
  };

  // Event Listeners
  nameInput.addEventListener('input', () => {
    if (isValidName(nameInput.value)) {
      clearError(fieldName, errorName);
      setValid(fieldName);
    } else {
      clearValid(fieldName);
    }
    validate();
  });

  nameInput.addEventListener('blur', () => {
    if (!nameInput.value) {
      clearError(fieldName, errorName);
      clearValid(fieldName);
    } else if (!isValidName(nameInput.value)) {
      setError(fieldName, errorName);
    } else {
      clearError(fieldName, errorName);
      setValid(fieldName);
    }
    validate();
  });

  // Mobile input character cleaning & validation
  mobileInput.addEventListener('input', () => {
    mobileInput.value = mobileInput.value.replace(/\D/g, ''); // strip non-digits
    if (isValidPhone()) {
      clearError(fieldMobile, errorMobile);
      setValid(fieldMobile);
    } else {
      clearValid(fieldMobile);
    }
    validate();
  });

  mobileInput.addEventListener('blur', () => {
    if (!mobileInput.value) {
      clearError(fieldMobile, errorMobile);
      clearValid(fieldMobile);
    } else if (!isValidPhone()) {
      setError(fieldMobile, errorMobile);
    } else {
      clearError(fieldMobile, errorMobile);
      setValid(fieldMobile);
    }
    validate();
  });

  iti.promise.then(() => {
    mobileInput.addEventListener('countrychange', () => {
      if (mobileInput.value) {
        if (isValidPhone()) {
          clearError(fieldMobile, errorMobile);
          setValid(fieldMobile);
        } else {
          setError(fieldMobile, errorMobile);
        }
      }
      validate();
    });
  });

  // Email input validation
  emailInput.addEventListener('input', () => {
    if (isValidEmail(emailInput.value)) {
      clearError(fieldEmail, errorEmail);
      setValid(fieldEmail);
    } else {
      clearValid(fieldEmail);
    }
    validate();
  });

  emailInput.addEventListener('blur', () => {
    if (!emailInput.value) {
      clearError(fieldEmail, errorEmail);
      clearValid(fieldEmail);
    } else if (!isValidEmail(emailInput.value)) {
      setError(fieldEmail, errorEmail);
    } else {
      clearError(fieldEmail, errorEmail);
      setValid(fieldEmail);
    }
    validate();
  });

  // Consent checkbox validation
  consentInput.addEventListener('change', () => {
    if (consentInput.checked) {
      if (errorConsent) errorConsent.classList.remove('is-visible');
    } else {
      if (errorConsent) errorConsent.classList.add('is-visible');
    }
    validate();
  });

  // Capture Final Page URL & UTM Parameters into 00NJ200000Eh81p
  const updateUtmField = () => {
    const utmInput = document.getElementById('00NJ200000Eh81p') || document.querySelector('input[name="00NJ200000Eh81p"]');
    if (utmInput) {
      const finalUrl = window.location.href;
      // Truncate to 255 characters max (Salesforce field limit)
      utmInput.value = finalUrl.substring(0, 255);
      console.log('%c [UTM / Final URL Captured] Field 00NJ200000Eh81p: ', 'background: #11243F; color: #D8AD73; font-weight: bold; padding: 2px 6px;', utmInput.value);
    }
  };

  // Run UTM capture on init
  updateUtmField();

  // Google Sheets Web App Endpoint URL (Optional fallback)
  const GOOGLE_SHEETS_SCRIPT_URL = window.GOOGLE_SHEETS_SCRIPT_URL || "YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL";

  // Form Submit Handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!validate()) return;

    updateDialCode();
    updateUtmField();

    const salutationInput = document.getElementById('sub-salutation');
    const payload = {
      salutation: salutationInput ? salutationInput.value : 'Mr.',
      name: nameInput.value.trim(),
      phone: mobileInput.value.trim(),
      fullPhone: iti ? iti.getNumber() : mobileInput.value.trim(),
      email: emailInput.value.trim(),
      timestamp: new Date().toISOString()
    };

    const originalBtnText = submit.textContent;
    submit.disabled = true;
    submit.textContent = 'SUBMITTING...';

    // Check if form is configured for Salesforce Web-to-Lead
    const oidInput = form.querySelector('input[name="oid"]');
    const sfActionUrl = form.action || "https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8";

    if (oidInput && oidInput.value) {
      console.group('%c [Salesforce Web-to-Lead] Submitting Lead to CRM ', 'background: #11243F; color: #D8AD73; font-size: 13px; font-weight: bold; padding: 4px 8px; border-radius: 3px;');
      console.log('Action Target URL:', sfActionUrl);

      const formData = new FormData(form);
      const urlEncodedData = new URLSearchParams();

      const logPayload = {};
      for (const [key, value] of formData.entries()) {
        urlEncodedData.append(key, value);
        logPayload[key] = value;
      }
      console.table(logPayload);

      try {
        // 1. Submit via URLSearchParams (application/x-www-form-urlencoded) fetch
        const sfResponse = await fetch(sfActionUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: urlEncodedData.toString()
        });
        console.log('✅ Salesforce Web-to-Lead URLSearchParams request dispatched.', sfResponse);
      } catch (sfErr) {
        console.error('❌ Error dispatching Salesforce Web-to-Lead fetch:', sfErr);
      }

      // 2. Hidden Iframe Form Post Backup (Ensures 100% native browser form POST to Salesforce without page reload)
      try {
        let iframe = document.getElementById('salesforce-target-iframe');
        if (!iframe) {
          iframe = document.createElement('iframe');
          iframe.name = 'salesforce-target-iframe';
          iframe.id = 'salesforce-target-iframe';
          iframe.style.display = 'none';
          document.body.appendChild(iframe);
        }
        form.target = 'salesforce-target-iframe';
        form.submit();
        console.log('✅ Salesforce Web-to-Lead native form.submit() dispatched to hidden iframe target.');
      } catch (iframeErr) {
        console.error('❌ Hidden iframe submit fallback error:', iframeErr);
      }
      console.groupEnd();
    } else {
      console.log('Standard form submission (non-Salesforce). Payload:', payload);
    }

    // Google Tag Manager Event Push
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'form_submission',
        'formName': 'Embassy Terazza RSVP Form',
        'salutation': payload.salutation
      });
      console.log('✅ GTM dataLayer form_submission event pushed.');
    } catch (gtmErr) {
      console.error('❌ GTM dataLayer push error:', gtmErr);
    }

    try {
      // Submit to Google Sheets (if configured)
      if (GOOGLE_SHEETS_SCRIPT_URL && GOOGLE_SHEETS_SCRIPT_URL !== "YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL") {
        await fetch(GOOGLE_SHEETS_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
    } catch (err) {
      console.error('Error submitting form to Google Sheets:', err);
    } finally {
      if (overlay) {
        overlay.classList.add('is-visible');
        overlay.setAttribute('aria-hidden', 'false');
        if (window.lenis) window.lenis.stop();
      }

      form.reset();
      clearValid(fieldName);
      clearValid(fieldMobile);
      clearValid(fieldEmail);
      submit.disabled = true;
      submit.textContent = originalBtnText;
    }
  });

  // Close overlay handler
  if (closeBtn && overlay) {
    closeBtn.addEventListener('click', () => {
      overlay.classList.remove('is-visible');
      overlay.setAttribute('aria-hidden', 'true');
      if (window.lenis) window.lenis.start();
    });
  }

  // Custom Salutation Dropdown Event Listeners
  const salutationDropdown = document.getElementById('salutation-dropdown');
  const salutationTrigger = document.getElementById('salutation-trigger');
  const salutationValue = document.getElementById('salutation-value');
  const salutationMenu = document.getElementById('salutation-menu');
  const salutationInput = document.getElementById('sub-salutation');

  if (salutationDropdown && salutationTrigger && salutationMenu) {
    salutationTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      salutationDropdown.classList.toggle('is-open');
      const isOpen = salutationDropdown.classList.contains('is-open');
      salutationTrigger.setAttribute('aria-expanded', isOpen);
    });

    const items = salutationMenu.querySelectorAll('.salutation-item');
    items.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const val = item.getAttribute('data-value');
        if (salutationValue) salutationValue.textContent = val;
        if (salutationInput) salutationInput.value = val;

        items.forEach(i => i.classList.remove('selected'));
        item.classList.add('selected');

        salutationDropdown.classList.remove('is-open');
        salutationTrigger.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('click', (e) => {
      if (!salutationDropdown.contains(e.target)) {
        salutationDropdown.classList.remove('is-open');
        salutationTrigger.setAttribute('aria-expanded', 'false');
      }
    });
  }
};

export default initSubscribeForm;
